// src/App.jsx
// BAMBOO OPERATIONS 2026 — BRAND REDESIGN — SAME ROUTES / FEATURES / PERMISSIONS

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  Suspense,
  lazy,
} from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { apiJSON } from "./apiBase";

import NavBar from "./components/NavBar.jsx";
import TableMetadataModal from "./TableMetadataModal";
import SelectContactsModal from "./SelectContactsModal";
import TableEditor from "./TableEditor";
import SearchByDate from "./SearchByDate";
import LiveTableFeed from "./LiveTableFeed";
import BirthdaySearch from "./BirthdaySearch";
import GuestSearch from "./GuestSearch";
import MergeByDateAll from "./pages/MergeByDateAll";

import { RefreshProvider, useRefresh } from "./context/RefreshContext";
import { useAuth } from "./context/AuthContext";
import PermGate from "./components/PermGate";

import "./App.css";
import bambooLogo from "./assets/bamboo-logo.jpg";

import LiveTableLogsByBranch from "./LiveTableLogsByBranch";
import Reservations from "./components/Reservations";
import IntaCompetitions from "./IntaCompetitions";
import ChatSeenContacts from "./components/ChatSeenContacts";

const Login = lazy(() => import("./pages/Login"));
const AllData = lazy(() => import("./AllData"));
const MenuPdfPriceEditor = lazy(() => import("./MenuPdfPriceEditor"));
const MenuPriceMapCalibrator = lazy(() => import("./MenuPriceMapCalibrator"));

const ERPHome = lazy(() => import("./ERPHome"));
const ERPInventory = lazy(() => import("./ERPInventory"));
const ERPCentralKitchen = lazy(() => import("./ERPCentralKitchen"));
const ERPAccounting = lazy(() => import("./ERPAccounting"));

/* =========================
   LocalStorage keys
========================= */
const LS_ERP_UNLOCK = "erp:unlock:v1";
const LS_WORKSPACE = "workspace:choice:v1";

const LS_SECTIONS = "home:sections:v1";
const LS_SHORTCUT_SIZE = "home:shortcutSize:v1";
const LS_THEME_MODE = "home:themeMode:v1";
const LS_CUSTOM_BG = "home:customBg:v1";
const LS_SECTION_DISPLAY_MODE = "home:sectionDisplay:v1";
const SS_ENTRY_REFRESHED = "app:entry-refreshed:v1";

/* =========================
   Branches
========================= */
const BRANCH_OPTIONS = [
  { value: "", label: "Auto (token/host)" },
  { value: "jounieh", label: "Jounieh" },
  { value: "tripoli", label: "Tripoli" },
  { value: "batroun", label: "Batroun" },
  { value: "zgharta", label: "Zgharta" },
  { value: "ehden", label: "Ehden" },
];

function normalizeUserBranchValues(raw) {
  const all = BRANCH_OPTIONS.filter((x) => x.value).map((x) => x.value);
  const arr = Array.isArray(raw)
    ? raw
    : String(raw || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

  const cleaned = arr.map((x) => String(x || "").trim().toLowerCase());
  if (cleaned.includes("*")) return all;
  return all.filter((b) => cleaned.includes(b));
}

/* =========================
   Default sections
========================= */
const DEFAULT_SECTIONS = [
  { id: "data-group", label: "Live Table Feed", enabled: true },
  { id: "insta", label: "Insta Competitions", enabled: true },
  { id: "reservations", label: "Reservations", enabled: true },
  { id: "table-logs", label: "All Branch Logs", enabled: true },
];

const SECTION_UI = {
  "data-group": {
    icon: "LT",
    eyebrow: "Live operations",
    description: "Watch active table activity and changes as they happen.",
  },
  insta: {
    icon: "IG",
    eyebrow: "Marketing",
    description: "Manage Instagram competitions and campaign activity.",
  },
  reservations: {
    icon: "RS",
    eyebrow: "Guest flow",
    description: "Open reservations and keep booking operations organized.",
  },
  "table-logs": {
    icon: "LG",
    eyebrow: "Multi-branch",
    description: "Review live table logs across every permitted branch.",
  },
};

/* =========================
   Utilities
========================= */
function safeLSGet(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

function safeLSSet(key, val) {
  try {
    localStorage.setItem(key, val);
  } catch {}
}

function safeJSONParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function getWorkspaceLabel(pathname) {
  if (pathname.startsWith("/erp")) return "ERP";
  if (pathname.startsWith("/home")) return "Home";
  if (pathname.startsWith("/birthdays")) return "Birthdays";
  if (pathname.startsWith("/guest-search")) return "Guest Search";
  if (pathname.startsWith("/reservations")) return "Reservations";
  if (pathname.startsWith("/competitions")) return "Competitions";
  if (pathname.startsWith("/chat-seen")) return "Chat Seen Contacts";
  if (pathname.startsWith("/all")) return "All Data";
  if (pathname.startsWith("/search-date")) return "Search By Date";
  if (pathname.startsWith("/merge-by-date-all")) return "Merge By Date";
  if (pathname.startsWith("/menu-price-editor")) return "Menu Price Editor";
  if (pathname.startsWith("/menu-price-calibrator")) return "Menu Calibrator";
  if (pathname.startsWith("/control")) return "Control";
  return "App";
}

/* =========================
   Global Server Loading Gate
========================= */
function ServerGate({ children }) {
  const [serverStatus, setServerStatus] = useState("checking");
  const [lastChecked, setLastChecked] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const checkServer = useCallback(async () => {
    try {
      await apiJSON("/health", { method: "GET" });
      setServerStatus("online");
      setLastChecked(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Server connection failed:", err);
      setServerStatus("offline");
      setRetryCount((v) => v + 1);
      setLastChecked(new Date().toLocaleTimeString());
    }
  }, []);

  useEffect(() => {
    checkServer();
    const interval = setInterval(checkServer, 5000);
    const handleOnline = () => checkServer();
    const handleFocus = () => checkServer();

    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkServer]);

  if (serverStatus !== "online") {
    const isChecking = serverStatus === "checking";

    return (
      <div className="server-gate-shell">
        <div className="server-gate-card">
          <div className="server-gate-brand">
            <img src={bambooLogo} alt="Bamboo Asian Cuisine" />
          </div>

          <div className={`server-state ${isChecking ? "checking" : "offline"}`}>
            <span className="server-state-dot" />
            {isChecking ? "Connecting to Bamboo" : "Backend unavailable"}
          </div>

          <h1>{isChecking ? "Opening your workspace" : "Waiting for the Bamboo server"}</h1>
          <p>
            {isChecking
              ? "We are checking the secure API connection before loading operational data."
              : "The interface will reconnect automatically. You can retry now without refreshing the whole browser."}
          </p>

          <div className="server-gate-progress" aria-hidden="true">
            <span />
          </div>

          <div className="server-gate-actions">
            <button type="button" className="btn primary" onClick={checkServer}>
              Retry connection
            </button>
            <button type="button" className="btn ghost" onClick={() => window.location.reload()}>
              Reload application
            </button>
          </div>

          <div className="server-gate-meta">
            <span>Automatic retry every 5 seconds</span>
            {lastChecked && <span>Last check {lastChecked}</span>}
            {retryCount > 0 && <span>{retryCount} reconnect attempt{retryCount === 1 ? "" : "s"}</span>}
          </div>
        </div>
      </div>
    );
  }

  return children;
}

/* =========================
   API status light
========================= */
function ApiStatusLight({ small = false }) {
  const [status, setStatus] = useState("checking");

  const checkApi = useCallback(async () => {
    try {
      await apiJSON("/saved-tables", { method: "GET" });
      setStatus("online");
    } catch (err) {
      console.error("API status check failed:", err);
      setStatus("offline");
    }
  }, []);

  useEffect(() => {
    checkApi();
    const id = setInterval(checkApi, 20000);
    return () => clearInterval(id);
  }, [checkApi]);

  const color =
    status === "online"
      ? "#22c55e"
      : status === "offline"
      ? "#ef4444"
      : "#f59e0b";

  const text =
    status === "online"
      ? "Server connected"
      : status === "offline"
      ? "Server offline"
      : "Checking server";

  return (
    <span
      className={`api-status-pill status-light-only status-${status} ${small ? "small" : ""}`}
      title={text}
      aria-label={text}
      role="status"
    >
      <span
        className="api-status-dot"
        style={{
          background: color,
          boxShadow: `0 0 12px ${color}`,
        }}
      />
    </span>
  );
}

/* =========================
   Error Boundary
========================= */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || "Something went wrong.",
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AppErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="page"
          style={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            padding: 20,
          }}
        >
          <div className="error-panel">
            <h2 style={{ marginTop: 0 }}>⚠️ Application error</h2>
            <p style={{ opacity: 0.9 }}>
              A screen crashed while loading. Reload the app to continue.
            </p>
            <pre className="error-pre">{this.state.message}</pre>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn primary"
                onClick={this.handleReload}
              >
                🔄 Reload app
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/* =========================
   Auth / Perm wrappers
========================= */
function RefreshOnceAfterEntry() {
  const { booting, isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (booting) return;

    if (!isAuthenticated) {
      try {
        sessionStorage.removeItem(SS_ENTRY_REFRESHED);
      } catch {
        // Session storage can be unavailable in locked-down browsers.
      }
      return;
    }

    if (location.pathname === "/login") return;

    try {
      if (sessionStorage.getItem(SS_ENTRY_REFRESHED) === "1") return;
      sessionStorage.setItem(SS_ENTRY_REFRESHED, "1");
    } catch {
      return;
    }

    window.location.reload();
  }, [booting, isAuthenticated, location.pathname]);

  return null;
}

function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return children;
}

function WithPerm({ perm, children }) {
  return (
    <PermGate
      perm={perm}
      fallback={
        <div className="page page-denied">
          <h2>⛔ 403 — Not allowed</h2>
          <p>You do not have permission to open this page.</p>
        </div>
      }
    >
      {children}
    </PermGate>
  );
}

function RequireErpCode({ children }) {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setAllowed(false);
      return;
    }

    const unlocked = localStorage.getItem(LS_ERP_UNLOCK) === "1";
    if (unlocked) {
      setAllowed(true);
      return;
    }

    const code = window.prompt("Enter CK / ERP access code:");
    if (code === "veru@123") {
      localStorage.setItem(LS_ERP_UNLOCK, "1");
      setAllowed(true);
    } else {
      alert("❌ Wrong code. Access denied.");
      localStorage.removeItem(LS_ERP_UNLOCK);
      localStorage.removeItem(LS_WORKSPACE);
      navigate("/", { replace: true });
    }
  }, [navigate]);

  if (!allowed) {
    return (
      <div className="page">
        <p>Checking ERP access…</p>
      </div>
    );
  }

  return children;
}

function ProtectedRoute({ perm, erp = false, children }) {
  if (erp) {
    return (
      <RequireAuth>
        <WithPerm perm={perm}>
          <RequireErpCode>{children}</RequireErpCode>
        </WithPerm>
      </RequireAuth>
    );
  }

  if (perm) {
    return (
      <RequireAuth>
        <WithPerm perm={perm}>{children}</WithPerm>
      </RequireAuth>
    );
  }

  return <RequireAuth>{children}</RequireAuth>;
}

/* =========================
   Small pages
========================= */
function Control() {
  return (
    <div className="page">
      <h1>Control Panel</h1>
      <p>Use the NavBar → More menu for sync options and settings.</p>
    </div>
  );
}

function AppSelector() {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ws = localStorage.getItem(LS_WORKSPACE);
    if (ws === "contacts") navigate("/home", { replace: true });
    else if (ws === "erp") navigate("/erp", { replace: true });
  }, [navigate]);

  const handleOpenHome = useCallback(() => {
    safeLSSet(LS_WORKSPACE, "contacts");
    navigate("/home");
  }, [navigate]);

  const handleChooseInventory = useCallback(() => {
    const unlocked = safeLSGet(LS_ERP_UNLOCK, "") === "1";
    let ok = unlocked;

    if (!unlocked) {
      const code = window.prompt("Enter CK / ERP access code:");
      if (code === "veru@123") {
        safeLSSet(LS_ERP_UNLOCK, "1");
        ok = true;
      } else {
        alert("❌ Wrong code. Access denied.");
        return;
      }
    }

    if (ok) {
      safeLSSet(LS_WORKSPACE, "erp");
      navigate("/erp");
    }
  }, [navigate]);

  return (
    <div className="workspace-gate">
      <div className="workspace-gate-panel">
        <div className="workspace-gate-brandline">
          <div className="workspace-logo-wrap">
            <img src={bambooLogo} alt="Bamboo Asian Cuisine" />
          </div>
          <ApiStatusLight />
        </div>

        <div className="workspace-gate-copy">
          <span className="workspace-kicker">Bamboo operations platform</span>
          <h1>Choose your workspace</h1>
          <p>
            Continue to guest and branch operations, or open the Central Kitchen ERP for stock,
            costing and accounting work.
          </p>
        </div>

        <div className="workspace-choice-grid">
          <button type="button" className="workspace-choice-card operations" onClick={handleOpenHome}>
            <span className="workspace-choice-code">OPS</span>
            <span className="workspace-choice-body">
              <strong>Operations & CRM</strong>
              <small>Tables, guests, reservations, birthdays, WhatsApp tools and live branch activity.</small>
            </span>
            <span className="workspace-choice-arrow">→</span>
          </button>

          <button type="button" className="workspace-choice-card erp" onClick={handleChooseInventory}>
            <span className="workspace-choice-code">ERP</span>
            <span className="workspace-choice-body">
              <strong>Central Kitchen ERP</strong>
              <small>Inventory, warehouse movement, central kitchen operations and accounting.</small>
            </span>
            <span className="workspace-choice-arrow">→</span>
          </button>
        </div>

        <div className="workspace-gate-foot">
          Your last workspace is remembered on this device. Use the main navigation to move between permitted areas.
        </div>
      </div>
    </div>
  );
}

/* =========================
   Shared modal shell
========================= */
function ModalShell({ title, onClose, compact = false, children }) {
  return (
    <div className="modal-overlay">
      <div className={`modal-content ${compact ? "compact" : ""}`}>
        <div className="modal-shell-head">
          <h3 className="modal-shell-title">{title}</h3>
          <button className="btn ghost small" onClick={onClose} type="button">
            ❌ Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SavedTablesModal({ open, tables, onClose, onLoad, onRefresh }) {
  if (!open) return null;

  return (
    <ModalShell title="📂 Saved Tables" compact onClose={onClose}>
      <div className="table-button-grid">
        {tables.length === 0 && (
          <div className="empty-hint">
            No saved tables found yet. Create a new table first.
          </div>
        )}

        {tables.map((name) => (
          <button
            key={name}
            className="table-btn"
            onClick={() => onLoad(name)}
            type="button"
          >
            {name}
          </button>
        ))}
      </div>

      <div className="modal-action-row">
        <button className="btn ghost" onClick={onClose} type="button">
          ❌ Close
        </button>
        <button className="btn primary" onClick={onRefresh} type="button">
          🔄 Refresh list
        </button>
      </div>
    </ModalShell>
  );
}

/* =========================
   CustomizeModal
========================= */
function CustomizeModal({
  open,
  onClose,
  sectionDisplayMode,
  setSectionDisplayMode,
  shortcutSize,
  setShortcutSize,
  themeMode,
  setThemeMode,
  customBg,
  setCustomBg,
  sections,
  moveSection,
  toggleSectionEnabled,
  handleCompactDb,
  isCompacting,
}) {
  if (!open) return null;

  return (
    <ModalShell title="⚙️ Customize Home Screen" onClose={onClose}>
      <div
        className="customize-body"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className="customize-row">
          <span className="customize-label">Section opening style</span>
          <div className="customize-controls">
            <button
              type="button"
              className={
                sectionDisplayMode === "window"
                  ? "btn primary small"
                  : "btn ghost small"
              }
              onClick={() => setSectionDisplayMode("window")}
            >
              🖼 Full screen
            </button>
            <button
              type="button"
              className={
                sectionDisplayMode === "inline"
                  ? "btn primary small"
                  : "btn ghost small"
              }
              onClick={() => setSectionDisplayMode("inline")}
            >
              📄 Inline
            </button>
          </div>
        </div>

        <div className="customize-row">
          <span className="customize-label">Shortcut size</span>
          <div className="customize-controls">
            {["small", "medium", "large"].map((size) => (
              <button
                key={size}
                type="button"
                className={
                  shortcutSize === size ? "btn primary small" : "btn ghost small"
                }
                onClick={() => setShortcutSize(size)}
              >
                {size[0].toUpperCase() + size.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="customize-row">
          <span className="customize-label">Theme</span>
          <div className="customize-controls">
            {[
              { id: "dark", label: "🌌 Dark" },
              { id: "forest", label: "🌿 Forest" },
              { id: "neon", label: "💜 Neon" },
              { id: "custom", label: "🎨 Custom" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                className={
                  themeMode === t.id ? "btn primary small" : "btn ghost small"
                }
                onClick={() => setThemeMode(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {themeMode === "custom" && (
          <div className="customize-row">
            <span className="customize-label">Background color</span>
            <div className="customize-controls">
              <input
                type="color"
                value={customBg}
                onChange={(e) => setCustomBg(e.target.value)}
                style={{
                  width: 44,
                  height: 28,
                  borderRadius: 999,
                  border: "1px solid rgba(148, 163, 184, 0.6)",
                  background: "transparent",
                  padding: 0,
                }}
              />
              <span style={{ fontSize: 11, opacity: 0.8 }}>
                Pick any background color
              </span>
            </div>
          </div>
        )}

        <div className="customize-row customize-sections-row">
          <span className="customize-label">Sections order</span>
          <div className="customize-sections-list">
            {sections.map((s, idx) => (
              <div key={s.id} className="customize-section-item">
                <span
                  className="customize-section-label"
                  style={{
                    opacity: s.enabled ? 1 : 0.45,
                    textDecoration: s.enabled ? "none" : "line-through",
                  }}
                >
                  {String(s.label ?? "")}
                </span>
                <div className="customize-section-buttons">
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => moveSection(s.id, -1)}
                    disabled={idx === 0}
                  >
                    ⬆
                  </button>
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => moveSection(s.id, +1)}
                    disabled={idx === sections.length - 1}
                  >
                    ⬇
                  </button>
                  <button
                    type="button"
                    className={s.enabled ? "btn ghost small" : "btn primary small"}
                    onClick={() => toggleSectionEnabled(s.id)}
                  >
                    {s.enabled ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="customize-row">
          <span className="customize-label">Database</span>
          <div className="customize-controls">
            <button
              type="button"
              className="btn primary small"
              onClick={handleCompactDb}
              disabled={isCompacting}
            >
              {isCompacting ? "🗜 Compacting…" : "🗜 Compact contacts.db"}
            </button>
            <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 8 }}>
              Runs compact_db_clone.py on the backend.
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8, textAlign: "right" }}>
        <button className="btn primary small" onClick={onClose} type="button">
          ✅ Done
        </button>
      </div>
    </ModalShell>
  );
}

/* =========================
   Collapsible
========================= */
function Collapsible({
  id,
  title,
  defaultCollapsed = true,
  externalOpenKey,
  onOpenSectionWindow,
  displayMode = "inline",
  children,
}) {
  const isWindowMode = displayMode === "window";
  const [collapsed, setCollapsed] = useState(
    isWindowMode ? true : !!defaultCollapsed
  );
  const innerRef = useRef(null);
  const [maxH, setMaxH] = useState(0);

  useEffect(() => {
    if (isWindowMode) {
      setCollapsed(true);
      setMaxH(0);
    }
  }, [isWindowMode]);

  useEffect(() => {
    if (!id || isWindowMode) return;
    if (externalOpenKey && externalOpenKey === id) setCollapsed(false);
  }, [externalOpenKey, id, isWindowMode]);

  useEffect(() => {
    if (isWindowMode) return;
    const el = innerRef.current;
    if (!el) return;
    setMaxH(collapsed ? 0 : (el.scrollHeight || 0) + 8);
  }, [collapsed, children, isWindowMode]);

  useEffect(() => {
    if (isWindowMode) return;
    const el = innerRef.current;
    if (!el || collapsed) return;
    if (typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      const target = innerRef.current;
      if (!target) return;
      setMaxH((target.scrollHeight || 0) + 8);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, isWindowMode]);

  const handleExpandClick = () => {
    if (isWindowMode) {
      onOpenSectionWindow?.(id);
      return;
    }
    setCollapsed((v) => !v);
  };

  const computedMaxH = isWindowMode ? 0 : maxH;

  return (
    <section className="card collapsible" style={{ marginTop: 12 }} id={id}>
      <div className="card-header collapsible-header">
        <h2 className="collapsible-title">{title}</h2>
        <button
          type="button"
          className="btn collapsible-toggle"
          aria-expanded={!collapsed}
          onClick={handleExpandClick}
        >
          {isWindowMode ? "🖼 Open" : collapsed ? "➕ Open" : "➖ Close"}
        </button>
      </div>

      <div
        className="collapsible-body"
        style={{
          overflow: "hidden",
          maxHeight: `${computedMaxH}px`,
          transition: isWindowMode ? "none" : "max-height 260ms ease",
        }}
      >
        <div
          ref={innerRef}
          className="collapsible-inner"
          style={{ padding: "8px 12px" }}
        >
          {children}
        </div>
      </div>
    </section>
  );
}

/* =========================
   Home
========================= */
function Home() {
  const { refreshKey, triggerRefresh } = useRefresh();
  const { user, activeBranch, availableBranches: allowedBranchValues, switchBranch } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [notice, setNotice] = useState("");
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [showWelcomePromo, setShowWelcomePromo] = useState(true);

  const noticeTimerRef = useRef(null);

  const [now, setNow] = useState(new Date());

  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [tableMeta, setTableMeta] = useState(null);

  const [tableData, setTableData] = useState(null);
  const [activeTable, setActiveTable] = useState(null);

  const [showTablesModal, setShowTablesModal] = useState(false);
  const [savedTables, setSavedTables] = useState([]);

  const [showEodReminder, setShowEodReminder] = useState(false);
  const SNOOZE_KEY = "eod:snoozeUntil";

  const [sectionDisplayMode, setSectionDisplayMode] = useState(() =>
    safeLSGet(LS_SECTION_DISPLAY_MODE, "window")
  );

  const [windowSection, setWindowSection] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  const [sections, setSections] = useState(() => {
    const raw = safeLSGet(LS_SECTIONS, "");
    if (!raw) return DEFAULT_SECTIONS;

    const parsed = safeJSONParse(raw, null);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_SECTIONS;

    const map = new Map(parsed.map((s) => [s.id, s]));
    return DEFAULT_SECTIONS.map((d) => {
      const saved = map.get(d.id) || {};
      return { ...d, ...saved, label: saved.label ?? d.label };
    });
  });

  const [shortcutSize, setShortcutSize] = useState(() =>
    safeLSGet(LS_SHORTCUT_SIZE, "medium")
  );

  const [themeMode, setThemeMode] = useState(() =>
    safeLSGet(LS_THEME_MODE, "dark")
  );

  const [customBg, setCustomBg] = useState(() =>
    safeLSGet(LS_CUSTOM_BG, "#020617")
  );

  const [showCustomize, setShowCustomize] = useState(false);
  const [isCompacting, setIsCompacting] = useState(false);

  const sectionRegistry = useMemo(
    () => ({
      "data-group": {
        id: "data-group",
        title: "Live Table Feed",
        render: () => <LiveTableFeed />,
      },
      insta: {
        id: "insta",
        title: "Insta Competitions",
        render: () => (
          <Suspense fallback="Loading…">
            <IntaCompetitions />
          </Suspense>
        ),
      },
      reservations: {
        id: "reservations",
        title: "Reservations",
        render: () => <Reservations />,
      },
      "table-logs": {
        id: "table-logs",
        title: "Live Table Logs (All Branches)",
        render: () => <LiveTableLogsByBranch />,
      },
    }),
    []
  );

  const notify = useCallback((msg) => {
    setNotice(msg);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(""), 3000);
  }, []);


  const visibleBranchOptions = useMemo(
    () => BRANCH_OPTIONS.filter((b) => b.value && allowedBranchValues.includes(b.value)),
    [allowedBranchValues.join("|")]
  );

  const setActiveBranch = useCallback(
    (branchValue) => {
      const requested = String(branchValue || "").trim().toLowerCase();
      if (!requested || !allowedBranchValues.includes(requested)) return;

      try {
        switchBranch(requested);
      } catch (err) {
        console.error("Branch switch failed:", err);
        notify(err?.message || "Branch switch failed");
      }
    },
    [allowedBranchValues.join("|"), switchBranch, notify]
  );

  useEffect(() => {
    document.title = `Bamboo • ${getWorkspaceLabel(location.pathname)}`;
  }, [location.pathname]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      notify("🌐 Back online");
    };
    const onOffline = () => {
      setIsOnline(false);
      notify("📴 You are offline");
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [notify]);

  useEffect(() => {
    const id = window.setTimeout(() => setShowWelcomePromo(false), 6200);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, []);

  useEffect(() => safeLSSet(LS_SECTIONS, JSON.stringify(sections)), [sections]);
  useEffect(() => safeLSSet(LS_SHORTCUT_SIZE, shortcutSize), [shortcutSize]);
  useEffect(() => safeLSSet(LS_THEME_MODE, themeMode), [themeMode]);
  useEffect(() => safeLSSet(LS_CUSTOM_BG, customBg), [customBg]);
  useEffect(
    () => safeLSSet(LS_SECTION_DISPLAY_MODE, sectionDisplayMode),
    [sectionDisplayMode]
  );

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const isAfter2315 = useMemo(() => {
    const hh = now.getHours();
    const mm = now.getMinutes();
    return (hh === 23 && mm >= 15) || hh > 23;
  }, [now]);

  useEffect(() => {
    const snoozeUntil = Number(sessionStorage.getItem(SNOOZE_KEY) || "0");
    setShowEodReminder(isAfter2315 && Date.now() > snoozeUntil);
  }, [isAfter2315, now]);

  const snoozeEod = useCallback((mins = 10) => {
    const until = Date.now() + mins * 60 * 1000;
    sessionStorage.setItem(SNOOZE_KEY, String(until));
    setShowEodReminder(false);
  }, []);

  const fetchSavedTables = useCallback(async () => {
    try {
      const data = await apiJSON("/saved-tables", { method: "GET" });
      setSavedTables(Array.isArray(data?.tables) ? data.tables : []);
    } catch (err) {
      console.error("Failed to fetch saved tables:", err);
      setSavedTables([]);
      notify("❌ Failed to load saved tables");
    }
  }, [notify]);

  useEffect(() => {
    fetchSavedTables();
  }, [fetchSavedTables, refreshKey, activeBranch]);

  const handleLoadTable = useCallback(
    async (tableName) => {
      try {
        const data = await apiJSON(`/table/${encodeURIComponent(tableName)}`, {
          method: "GET",
        });

        if (data?.error) {
          alert(`❌ Failed to load table: ${data.error}`);
          return;
        }

        setActiveTable(tableName);
        setTableData(data);
        setShowTablesModal(false);
      } catch (err) {
        console.error("Failed to load table:", err);
        alert("❌ Failed to load table");
      }
    },
    []
  );

  const mergeAllTables = useCallback(async () => {
    if (!window.confirm("Merge all saved tables into one table.db?")) return;

    try {
      const data = await apiJSON("/merge-tables", { method: "POST" });
      alert(data?.message || "✅ Merge complete");
      triggerRefresh();
    } catch (err) {
      console.error("Failed to merge tables:", err);
      alert("❌ Failed to merge tables.");
    }
  }, [triggerRefresh]);

  const runEndOfDay = useCallback(async () => {
    if (!isAfter2315) {
      notify("⏳ End of Day is available after 23:15.");
      return;
    }
    if (!window.confirm("Run End of Day now?")) return;

    try {
      const data = await apiJSON("/end-of-day", { method: "POST" });
      alert(
        `✅ End of Day complete\nInserted: ${data?.inserted ?? 0}\nSkipped: ${
          data?.skipped ?? 0
        }`
      );
      triggerRefresh();
      snoozeEod(240);
    } catch (e) {
      console.error("EOD error:", e);
      alert("❌ Network error running End of Day.");
    }
  }, [isAfter2315, notify, snoozeEod, triggerRefresh]);

  const handleCompactDb = useCallback(async () => {
    if (
      !window.confirm(
        "Compact contacts.db on the server now?\n\nThis may lock the DB for a short time."
      )
    ) {
      return;
    }

    setIsCompacting(true);

    try {
      const data = await apiJSON("/compact-db", { method: "POST" });

      const before = data?.before_size_h || data?.before_size || "";
      const after = data?.after_size_h || data?.after_size || "";
      const saved = data?.saved_size_h || data?.saved_size || "";

      if (before && after) {
        notify(
          `✅ Compacted DB: ${before} → ${after}${
            saved ? ` (saved ${saved})` : ""
          }`
        );
      } else {
        notify(data?.message || "✅ Database compacted");
      }

      triggerRefresh();
    } catch (err) {
      console.error("Compact DB failed:", err);
      notify("❌ Failed to compact DB");
    } finally {
      setIsCompacting(false);
    }
  }, [notify, triggerRefresh]);

  const openSectionWindow = useCallback(
    (sectionId) => {
      if (sectionDisplayMode !== "window") return;
      setWindowSection(sectionId);
    },
    [sectionDisplayMode]
  );

  const openSectionFromShortcut = useCallback(
    (sectionId) => {
      if (sectionDisplayMode === "inline") setActiveSection(sectionId);
      else setWindowSection(sectionId);
    },
    [sectionDisplayMode]
  );

  const getWindowTitle = useCallback(
    (sectionId) => {
      const found = sections.find((s) => s.id === sectionId);
      return found?.label || sectionRegistry[sectionId]?.title || "";
    },
    [sections, sectionRegistry]
  );

  const renderSectionComponent = useCallback(
    (sectionId) => sectionRegistry[sectionId]?.render?.() ?? null,
    [sectionRegistry]
  );

  const moveSection = useCallback((id, direction) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;

      const nextIndex = idx + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;

      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(nextIndex, 0, item);
      return copy;
    });
  }, []);

  const toggleSectionEnabled = useCallback((id) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s))
    );
  }, []);

  const appRootClass = `app-root theme-${themeMode}`;
  const appRootStyle =
    themeMode === "custom" ? { background: customBg } : undefined;

  const visibleSections = useMemo(
    () => sections.filter((s) => s.enabled),
    [sections]
  );

  useEffect(() => {
    if (sectionDisplayMode === "window") setActiveSection(null);
    if (sectionDisplayMode === "inline") setWindowSection(null);
  }, [sectionDisplayMode]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      const tag = target?.tagName?.toLowerCase?.();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable;

      if (isTyping) return;

      if (e.key === "Escape") {
        if (windowSection) setWindowSection(null);
        else if (showCustomize) setShowCustomize(false);
        else if (showTablesModal) setShowTablesModal(false);
        else if (showMetadataModal) setShowMetadataModal(false);
      }

      if (e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        setShowMetadataModal(true);
      }

      if (e.altKey && e.key.toLowerCase() === "t") {
        e.preventDefault();
        setShowTablesModal(true);
      }

      if (e.altKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        triggerRefresh();
        notify("🔄 Refreshed");
      }

      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        setShowCustomize((v) => !v);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    windowSection,
    showCustomize,
    showTablesModal,
    showMetadataModal,
    triggerRefresh,
    notify,
  ]);

  const displayName = useMemo(() => {
    const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
    return full || user?.name || user?.username || user?.email || "Bamboo Team";
  }, [user]);

  const greeting = useMemo(() => {
    const hour = now.getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  }, [now]);

  const activeBranchLabel = useMemo(() => {
    return BRANCH_OPTIONS.find((b) => b.value === activeBranch)?.label || activeBranch || "Select branch";
  }, [activeBranch]);

  return (
    <div className={appRootClass} style={appRootStyle}>
      <NavBar
        onCreateNewTable={() => setShowMetadataModal(true)}
        onOpenTables={() => setShowTablesModal(true)}
        onMergeAll={mergeAllTables}
        onEndOfDay={runEndOfDay}
        currentTableName={activeTable}
      />

      <main className="bamboo-app-main">
        <div className="container bamboo-dashboard">
          {!isOnline && (
            <div className="offline-banner">
              <strong>Offline mode</strong>
              <span>Your browser is offline. Read-only data may remain visible, but server actions can fail.</span>
            </div>
          )}

          {showEodReminder && (
            <div className="eod-reminder bamboo-reminder">
              <div>
                <span className="reminder-kicker">End of day</span>
                <strong>Closing time check</strong>
                <small>Run End of Day before the branch closes its operational session.</small>
              </div>
              <div className="eod-actions">
                <button className="btn ghost" onClick={() => snoozeEod(10)} type="button">
                  Snooze 10 min
                </button>
                <button className="btn primary" onClick={runEndOfDay} type="button">
                  Run End of Day
                </button>
              </div>
            </div>
          )}

          <section className="bamboo-hero">
            <div className="bamboo-hero-copy">
              <div className="hero-brand-row">
                <div className="hero-logo-card">
                  <img src={bambooLogo} alt="Bamboo Asian Cuisine" />
                </div>
                <div>
                  <span className="hero-eyebrow">Operations command center</span>
                  <span className="hero-branch-line">{activeBranchLabel}</span>
                </div>
              </div>

              {showWelcomePromo && (
                <div className="welcome-promo" role="status" aria-live="polite">
                  <span className="welcome-promo-mark" aria-hidden="true">✦</span>
                  <div className="welcome-promo-copy">
                    <span className="welcome-promo-kicker">Welcome back</span>
                    <h1>{greeting}, {displayName.split(" ")[0]}</h1>
                  </div>
                  <button
                    type="button"
                    className="welcome-promo-close"
                    onClick={() => setShowWelcomePromo(false)}
                    aria-label="Dismiss welcome message"
                    title="Dismiss"
                  >
                    ×
                  </button>
                  <span className="welcome-promo-progress" aria-hidden="true" />
                </div>
              )}
              <p>
                Manage daily guest operations, tables and branch activity from one focused workspace.
                Branch access follows your user permissions.
              </p>

              <div className="hero-status-row">
                <span className="hero-status-chip branch"><i /> {activeBranchLabel}</span>
                <ApiStatusLight small />
                <span className="hero-status-chip time">
                  {now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  <b>{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</b>
                </span>
              </div>
            </div>

            <div className="bamboo-hero-actions">
              <button type="button" className="hero-primary-action" onClick={() => setShowMetadataModal(true)}>
                <span className="action-mark">+</span>
                <span><strong>New table</strong><small>Create a guest table</small></span>
              </button>
              <button type="button" className="hero-secondary-action" onClick={() => setShowTablesModal(true)}>
                <span className="action-mark">↗</span>
                <span><strong>Saved tables</strong><small>Continue existing work</small></span>
              </button>
              <button type="button" className="hero-secondary-action" onClick={() => navigate("/guest-search")}>
                <span className="action-mark">⌕</span>
                <span><strong>Guest search</strong><small>Find a customer quickly</small></span>
              </button>
            </div>
          </section>

          <section className="bamboo-commandbar" aria-label="Workspace controls">
            <div className="command-branch-block">
              <span className="command-label">Active branch</span>
              <select
                value={activeBranch || ""}
                onChange={(e) => {
                  setActiveBranch(e.target.value);
                  triggerRefresh();
                }}
                className="command-branch-select"
                aria-label="Switch active branch"
              >
                {visibleBranchOptions.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
              <span className="command-access-note">
                {visibleBranchOptions.length} permitted branch{visibleBranchOptions.length === 1 ? "" : "es"}
              </span>
            </div>

            <div className="command-actions">
              <button
                type="button"
                className="btn ghost small"
                onClick={() => {
                  triggerRefresh();
                  notify("Data refreshed");
                }}
              >
                Refresh
              </button>
              <button type="button" className="btn ghost small" onClick={() => setShowCustomize(true)}>
                Customize
              </button>
            </div>
          </section>

          <section className="dashboard-section-head">
            <div>
              <span className="section-kicker">Daily workspace</span>
              <h2>Operational modules</h2>
            </div>
            <p>Open the modules you use most. Your layout and display mode are saved on this device.</p>
          </section>

          <div className="bamboo-module-grid">
            {visibleSections.map((s, index) => {
              const ui = SECTION_UI[s.id] || {};
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`bamboo-module-card shortcut-${shortcutSize}`}
                  onClick={() => openSectionFromShortcut(s.id)}
                >
                  <span className="module-index">0{index + 1}</span>
                  <span className="module-icon">{ui.icon || "OP"}</span>
                  <span className="module-copy">
                    <small>{ui.eyebrow || "Bamboo module"}</small>
                    <strong>{String(s.label ?? "")}</strong>
                    <em>{ui.description || `Open ${String(s.label ?? "")}`}</em>
                  </span>
                  <span className="module-arrow">↗</span>
                </button>
              );
            })}

            {visibleSections.length === 0 && (
              <div className="empty-hint">
                No dashboard modules are enabled. Open Customize to restore a section.
              </div>
            )}
          </div>

          {sectionDisplayMode === "inline" && activeSection && (
            <div className="inline-module-panel">
              <Collapsible
                id={activeSection}
                title={getWindowTitle(activeSection)}
                defaultCollapsed={false}
                externalOpenKey={activeSection}
                displayMode="inline"
                onOpenSectionWindow={openSectionWindow}
              >
                {renderSectionComponent(activeSection)}
              </Collapsible>
            </div>
          )}
        </div>

        {showMetadataModal && (
          <TableMetadataModal
            onSubmit={(meta) => {
              setTableMeta(meta);
              setShowMetadataModal(false);
            }}
            onClose={() => setShowMetadataModal(false)}
          />
        )}

        {tableMeta && (
          <SelectContactsModal
            key={`${tableMeta.table_number || tableMeta.table_name || "table"}-${refreshKey}`}
            tableMeta={tableMeta}
            onClose={() => {
              setTableMeta(null);
              triggerRefresh();
            }}
          />
        )}

        {tableData && (
          <div className="modal-overlay editor-overlay">
            <div className="modal-content fullscreen-editor">
              <TableEditor
                tableName={activeTable}
                initialData={tableData}
                onClose={() => {
                  setActiveTable(null);
                  setTableData(null);
                  triggerRefresh();
                }}
              />
            </div>
          </div>
        )}

        <SavedTablesModal
          open={showTablesModal}
          tables={savedTables}
          onClose={() => setShowTablesModal(false)}
          onLoad={handleLoadTable}
          onRefresh={fetchSavedTables}
        />

        <CustomizeModal
          open={showCustomize}
          onClose={() => setShowCustomize(false)}
          sectionDisplayMode={sectionDisplayMode}
          setSectionDisplayMode={setSectionDisplayMode}
          shortcutSize={shortcutSize}
          setShortcutSize={setShortcutSize}
          themeMode={themeMode}
          setThemeMode={setThemeMode}
          customBg={customBg}
          setCustomBg={setCustomBg}
          sections={sections}
          moveSection={moveSection}
          toggleSectionEnabled={toggleSectionEnabled}
          handleCompactDb={handleCompactDb}
          isCompacting={isCompacting}
        />

        {windowSection && (
          <div className="modal-overlay editor-overlay">
            <div className="modal-content fullscreen-editor bamboo-window-panel">
              <div className="window-head">
                <div>
                  <span className="section-kicker">Bamboo workspace</span>
                  <h3>{getWindowTitle(windowSection)}</h3>
                </div>
                <button className="btn ghost small" onClick={() => setWindowSection(null)} type="button">
                  Close
                </button>
              </div>
              <div className="window-scroll-area">{renderSectionComponent(windowSection)}</div>
            </div>
          </div>
        )}

        {notice && <div className="toast">{notice}</div>}
      </main>
    </div>
  );

}

/* =========================
   Route table
========================= */
function AppRoutes() {
  // One authoritative branch comes from AuthContext. The route key changes on
  // branch switch so branch-aware screens cannot retain stale data.
  const { activeBranch: routeBranch } = useAuth();

  const appRoutes = [
    {
      path: "/",
      element: <AppSelector />,
    },
    {
      path: "/home",
      perm: "nav_home",
      element: <Home />,
    },
    {
      path: "/all",
      perm: "nav_all_data",
      element: <AllData />,
    },
    {
      path: "/search-date",
      perm: "nav_search_date",
      element: <SearchByDate />,
    },
    {
      path: "/merge-by-date-all",
      perm: "nav_merge_by_date_all",
      element: <MergeByDateAll />,
    },
    {
      path: "/reservations",
      perm: "nav_reservations",
      element: <Reservations />,
    },
    {
      path: "/birthdays",
      perm: "nav_birthdays",
      element: <BirthdaySearch />,
    },
    {
      path: "/guest-search",
      perm: "nav_guest_search",
      element: <GuestSearch />,
    },
    {
  path: "/competitions",
  perm: "nav_competitions",
  element: <IntaCompetitions />,
},

{
  path: "/chat-seen",
  perm: "nav_control",
  element: <ChatSeenContacts />,
},

{
  path: "/control",
  perm: "nav_control",
  element: <Control />,
},
    {
      path: "/menu-price-editor",
      perm: "nav_menu_prices",
      element: <MenuPdfPriceEditor />,
    },
    {
      path: "/menu-price-calibrator",
      perm: "nav_menu_calibrator",
      element: <MenuPriceMapCalibrator />,
    },
    {
      path: "/erp",
      perm: "nav_inventory",
      erp: true,
      element: <ERPHome />,
    },
    {
      path: "/erp/inventory",
      perm: "nav_inventory",
      erp: true,
      element: <ERPInventory />,
    },
    {
      path: "/erp/ck",
      perm: "nav_inventory",
      erp: true,
      element: <ERPCentralKitchen />,
    },
    {
      path: "/erp/accounting",
      perm: "nav_inventory",
      erp: true,
      element: <ERPAccounting />,
    },
  ];

  return (
    <>
      <RefreshOnceAfterEntry />
      <Routes key={`active-branch:${routeBranch || "none"}`}>
        {appRoutes.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              <ProtectedRoute perm={r.perm} erp={r.erp}>
                {r.element}
              </ProtectedRoute>
            }
          />
        ))}

        <Route path="/login" element={<Login />} />

        <Route
          path="*"
          element={
            <RequireAuth>
              <Navigate to="/" replace />
            </RequireAuth>
          }
        />
      </Routes>
    </>
  );
}

/* =========================
   Root App
========================= */
function App() {
  return (
    <AppErrorBoundary>
      <ServerGate>
        <RefreshProvider>
          <Router>
            <Suspense
              fallback={
                <div
                  style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#020617",
                    color: "white",
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  Loading application...
                </div>
              }
            >
              <AppRoutes />
            </Suspense>
          </Router>
        </RefreshProvider>
      </ServerGate>
    </AppErrorBoundary>
  );
}

export default App;
